/**
 * @file: TaskCardRenderer.tsx
 *
 * An order / service-record task card (`order_card` | `service_record_card`).
 * Full form delegates to `ChatTaskCardCompact` (snapshot + live revalidate);
 * `quoted` is a compact "icon + title" chip.
 */

import { describeChatCard } from '@euler/app/flows/chat/utils/chatCardDescriptor';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ChatMessagePayloadOrderCard,
  ChatMessagePayloadServiceRecordCard,
} from '@euler/model/chat';
import { ClipboardList, Wrench } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import { ChatTaskCardCompact } from '../ChatTaskCardCompact';
import { CardQuoteChip } from './cardPrimitives';
import { PayloadRendererProps } from './types';

type TaskCardPayload =
  | ChatMessagePayloadOrderCard
  | ChatMessagePayloadServiceRecordCard;

interface Props extends PayloadRendererProps<TaskCardPayload> {
  onOpenCard?: (payload: TaskCardPayload) => void;
}

export const TaskCardRenderer: FC<Props> = memo(
  ({ payload, quoted, onOpenCard }) => {
    const { rpx } = useResponsiveContext();

    const handlePress = useCallback(
      () => onOpenCard?.(payload),
      [onOpenCard, payload],
    );

    if (quoted) {
      const view = describeChatCard(payload);
      const Icon =
        payload.type === 'service_record_card' ? Wrench : ClipboardList;
      return (
        <CardQuoteChip
          icon={<Icon color="#3b82f6" size={rpx(15)} />}
          label={view.title}
        />
      );
    }

    return <ChatTaskCardCompact payload={payload} onPress={handlePress} />;
  },
);
